import React, { useEffect } from "react";
import {
	Asset,
	Button,
	ChevronLeftIcon,
	ChevronRightIcon,
	CrossIcon,
	FONT_COLOR,
	FONT_SIZE,
	PaddedContainer,
	SPACING,
	Text,
} from "@disco/disco_core";

import "./lead-gen-header.css";
import accessibilityProvider from "../../utils/accessibilityProvider";
import useExperience from "../../hooks/useExperience";
import usePing, { PING_TYPES } from "../../hooks/usePing";
import useTrack, { eventTypes } from "../../hooks/useTrack";
import { WIDGET_ELEMENT_CLICK_TARGET } from "../../event_conf";
import { SNOWPLOW_SCHEMA_DATA_KEY } from "../../utils/snowplowEventMigrator";
import useCustomization, {
	CUSTOMIZATION_DETAIL,
	CUSTOMIZATION_TYPE,
} from "../../hooks/useCustomization";
import { MESSAGE_TYPES } from "../../hooks/useMessage";
import message from "../../utils/message";
import useLeadGenV0Config from "../../hooks/useLeadGenV0Config";
import brandOfferEntityMapper from "../../utils/brandOfferEntityMapper";
import { WIDGET_TYPE, WIDGET_VERSION } from "../../conf";

const LeadGenHeaderNav = ({
	totalIdx = 0,
	currIdx = 0,
	disableEngagement,
} = {}) => {
	const isFirstIdx = currIdx === 0;
	const isLastIdx = currIdx === totalIdx - 1;

	const { ping: onNext } = usePing({
		key: PING_TYPES.LEAD_GEN_CAROUSEL_NEXT,
	});
	const { ping: onPrev } = usePing({
		key: PING_TYPES.LEAD_GEN_CAROUSEL_PREV,
	});

	const track = useTrack();

	const handlePrev = () => {
		if (isFirstIdx || disableEngagement) return;

		track(eventTypes.WIDGET_ELEMENT_CLICK, {
			[SNOWPLOW_SCHEMA_DATA_KEY]: {
				horizontalPosition: currIdx,
			},
			initiatingTarget: WIDGET_ELEMENT_CLICK_TARGET.INLINE_NAV_BACKWARD,
		});
		if (typeof onPrev !== "function") return;
		onPrev();
	};

	const handleNext = () => {
		if (isLastIdx || disableEngagement) return;

		track(eventTypes.WIDGET_ELEMENT_CLICK, {
			[SNOWPLOW_SCHEMA_DATA_KEY]: {
				horizontalPosition: currIdx,
			},
			initiatingTarget: WIDGET_ELEMENT_CLICK_TARGET.INLINE_NAV_FORWARD,
		});
		if (typeof onNext !== "function") return;
		onNext();
	};

	return (
		<PaddedContainer flexContent marginLeft={SPACING.REGULAR}>
			<Button
				secondary
				marginRight={SPACING.SMALL}
				circular
				style={{ "--size": "40px" }}
				disabled={isFirstIdx}
				onClick={handlePrev}
				{...accessibilityProvider({
					role: "button",
				})}
			>
				<Asset size={FONT_SIZE.BODY}>
					<ChevronLeftIcon />
				</Asset>
			</Button>
			<Button
				secondary
				circular
				style={{ "--size": "40px" }}
				disabled={isLastIdx}
				onClick={handleNext}
				{...accessibilityProvider({
					role: "button",
				})}
			>
				<ChevronRightIcon />
			</Button>
		</PaddedContainer>
	);
};

export const LeadGenHeaderFullScreenControls = ({
	defaultEventProps,
	activeBrand,
	activeBrandIdx,
}) => {
	const track = useTrack();

	const handleClose = () => {
		track(eventTypes.WIDGET_ELEMENT_CLICK, {
			...defaultEventProps,
			[SNOWPLOW_SCHEMA_DATA_KEY]: {
				...brandOfferEntityMapper({
					brand: activeBrand,
				}),
				horizontalPosition: activeBrandIdx,
			},
			initiatingTarget: WIDGET_ELEMENT_CLICK_TARGET.CLOSE_BUTTON,
		});
		message(MESSAGE_TYPES.CLOSE_WIDGET_FULLSCREEN);
	};

	return (
		<PaddedContainer flexContent vPadding={SPACING.TINY} tightTop>
			<Text size={"body"} color="mid">
				Your order is confirmed
			</Text>
			<Asset
				clickable
				size={26}
				style={{
					background: "var(--disco-bg-default",
					borderRadius: 100,
				}}
				color="dark"
				marginLeft={"auto"}
				vPadding={SPACING.MICRO}
				hPadding={SPACING.MICRO}
				onClick={handleClose}
			>
				<CrossIcon />
			</Asset>
		</PaddedContainer>
	);
};

const LeadGenHeader = ({
	totalIdx = 0,
	claimedCount = 0,
	currIdx = 0,
	triggerWidgetResize,
	disableNavEngagement = false,
	showOfferSlot = true,
}) => {
	const { isLargeWidget } = useLeadGenV0Config();

	const { isNurtureV1, activeWidgetVersion } = useExperience();

	const { applyCustomizationOrFallback } = useCustomization();

	useEffect(() => {
		triggerWidgetResize();
	}, [triggerWidgetResize]);

	const getPrimaryHeading = () => {
		let _primaryHeading =
			claimedCount === 0
				? `Special offers just for you`
				: `Your special offer is claimed!`;

		if (
			activeWidgetVersion === WIDGET_VERSION[WIDGET_TYPE.LEAD_GEN][0.65]
		) {
			_primaryHeading = "Your order comes with rewards";
		}

		if (
			activeWidgetVersion === WIDGET_VERSION[WIDGET_TYPE.LEAD_GEN][0.7] &&
			!showOfferSlot
		) {
			_primaryHeading = "Trending brands we think you'll love";
		}

		return applyCustomizationOrFallback(
			CUSTOMIZATION_DETAIL[CUSTOMIZATION_TYPE.CHECKOUT].WIDGET_HEADING,
			_primaryHeading
		);
	};

	const getSecondaryHeading = () => {
		let _secondaryHeading =
			claimedCount === 0
				? `As a thank you for your recent purchase`
				: "Discover more deals to save on top brands";

		if (
			activeWidgetVersion === WIDGET_VERSION[WIDGET_TYPE.LEAD_GEN][0.65]
		) {
			_secondaryHeading = "Unlock exclusive offers before they’re gone";
		}

		return applyCustomizationOrFallback(
			CUSTOMIZATION_DETAIL[CUSTOMIZATION_TYPE.CHECKOUT].WIDGET_SUBHEADING,
			_secondaryHeading
		);
	};

	const primaryHeading = getPrimaryHeading();
	const secondaryHeading = getSecondaryHeading();

	return (
		<PaddedContainer flexContent>
			<PaddedContainer marginRight={"auto"}>
				<Text
					size={FONT_SIZE.SUB_TITLE}
					className="lead-gen-header-title"
					{...accessibilityProvider({
						role: "heading",
						name: primaryHeading,
						level: 1,
					})}
				>
					{primaryHeading}
				</Text>
				<Text
					size={isLargeWidget ? "body" : "label"}
					color={FONT_COLOR.MID}
					className="lead-gen-header-subtitle"
					{...accessibilityProvider({
						role: "heading",
						name: secondaryHeading,
						level: 2,
					})}
				>
					{secondaryHeading}
				</Text>
			</PaddedContainer>
			{isNurtureV1 && isLargeWidget && (
				<LeadGenHeaderNav
					totalIdx={totalIdx}
					currIdx={currIdx}
					disableEngagement={disableNavEngagement}
				/>
			)}
		</PaddedContainer>
	);
};
export default LeadGenHeader;
